import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone, Recycle, ShieldCheck, Truck, User } from "lucide-react";
import Seo from "../components/Seo";
import { CTA } from "../components/CTA";
import { BUSINESS, IMAGES } from "../lib/site";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us | Velocity Electrical Surplus — Nationwide Electrical Surplus Buyers"
        description="Velocity Electrical Surplus is an independent nationwide buyer of surplus industrial electrical equipment and wire, based in New Baltimore, Michigan."
      />

      <section className="relative overflow-hidden">
        <img
          src={IMAGES.switchgear}
          alt="Industrial electrical switchgear"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-ves-black/85" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <p className="font-heading text-xs font-bold tracking-[0.25em] text-ves-green">ABOUT US</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-black text-white sm:text-5xl text-balance">
            AN INDEPENDENT, NATIONWIDE ELECTRICAL SURPLUS BUYER
          </h1>
        </div>
      </section>

      <section className="bg-ves-black py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          <div className="space-y-5 lg:col-span-2">
            <p className="text-base leading-relaxed text-white/80 sm:text-lg">
              Velocity Electrical Surplus specializes in purchasing new, used, obsolete, and
              surplus industrial electrical equipment throughout the United States. Based in
              New Baltimore, Michigan, we buy everything from individual circuit breakers and
              leftover electrical wire to complete warehouses, switchgear systems,
              transformers, and large industrial liquidations.
            </p>
            <p className="text-base leading-relaxed text-white/70">
              We work with electrical contractors, commercial electricians, industrial
              facilities, manufacturing plants, demolition contractors, warehouses, and
              businesses that are closing, relocating, or upgrading. Our goal is simple:
              competitive cash offers, fast purchasing decisions, and a selling process that
              is fast, easy, and straightforward — we can even arrange freight, loading, and
              trucking for qualifying purchases.
            </p>
            <div className="grid gap-5 sm:grid-cols-3">
              {[
                { icon: Recycle, t: "Reuse First", d: "We identify equipment and wire whose value goes beyond scrap content." },
                { icon: Truck, t: "We Come To You", d: "Nationwide pickups with freight and loading coordination." },
                { icon: ShieldCheck, t: "Straightforward Deals", d: "Send pictures, receive an offer, arrange the transaction." },
              ].map((v) => (
                <div key={v.t} className="border border-white/10 bg-ves-charcoal/50 p-5" data-testid={`about-value-${v.t.replace(/\s/g, "-")}`}>
                  <v.icon className="h-7 w-7 text-ves-green" />
                  <h2 className="mt-3 font-heading text-base font-black text-white">{v.t}</h2>
                  <p className="mt-1.5 text-sm text-white/65">{v.d}</p>
                </div>
              ))}
            </div>
            <p className="border-l-4 border-ves-green bg-ves-forest/40 p-5 text-sm leading-relaxed text-white/80">
              Velocity Electrical Surplus is an independent purchasing company. We are not
              affiliated with, endorsed by, or sponsored by Square D, Schneider Electric,
              Siemens, Eaton, Cutler-Hammer, Allen-Bradley, ABB, General Electric,
              Westinghouse, or any other manufacturer — manufacturer names are used only to
              identify the equipment we purchase.
            </p>
          </div>

          <aside className="border border-white/10 bg-ves-charcoal/50 p-6 sm:p-8">
            <h2 className="font-heading text-lg font-black text-white">CONTACT</h2>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              <li className="flex items-center gap-3">
                <User className="h-5 w-5 shrink-0 text-ves-green" /> Purchasing Manager: {BUSINESS.manager}
              </li>
              <li>
                <a href={BUSINESS.phoneTel} className="flex items-center gap-3 transition-colors duration-150 hover:text-ves-green" data-testid="about-phone-link">
                  <Phone className="h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={BUSINESS.emailHref} className="flex items-center gap-3 break-all transition-colors duration-150 hover:text-ves-green" data-testid="about-email-link">
                  <Mail className="h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.email}
                </a>
              </li>
              <li>
                <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 transition-colors duration-150 hover:text-ves-green" data-testid="about-address-link">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.address}
                </a>
              </li>
            </ul>
            <Link
              to="/sell-your-surplus"
              className="mt-6 flex items-center justify-center gap-2 bg-ves-green px-5 py-3.5 font-heading text-sm font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
              data-testid="about-get-offer-btn"
            >
              GET AN OFFER <ArrowRight className="h-4 w-4" />
            </Link>
          </aside>
        </div>
      </section>

      <CTA />
    </div>
  );
}
